import { Env } from '../env.ts'
import { type Logger } from '@core/logging/logger.ts'

/** Invoked when the browser notification permission changes out-of-band. @internal */
export type PermissionChangeListener = () => void | Promise<void>

/**
 * Watches the browser notification permission for out-of-band changes — e.g. the user
 * toggling the site setting from "Allow" to "Block" — via the Permissions API `change`
 * event, and invokes the listener so the SDK can reconcile immediately rather than waiting
 * for the next page load.
 *
 * No-op where `navigator.permissions` (or the `notifications` descriptor) is unavailable;
 * those browsers continue to reconcile at load time only.
 *
 * @internal
 */
export class PermissionChangeObserver {
    public static get _isSupported(): boolean {
        return 'permissions' in Env._navigator && typeof Env._navigator.permissions?.query === 'function'
    }

    private status: PermissionStatus | null = null

    private readonly handler = (): void => {
        void this.listener()
    }

    public constructor(
        private readonly listener: PermissionChangeListener,
        private readonly logger: Logger,
    ) {}

    public async _start(): Promise<void> {
        if (this.status) {
            return
        }

        try {
            this.status = await Env._navigator.permissions.query({ name: 'notifications' as PermissionName })
            this.status.addEventListener('change', this.handler)
            this.logger.debug('Watching notification permission for changes')
        } catch (_err) {
            this.logger.debug(
                'Permissions API unavailable for notifications; permission changes detected at next load only',
            )
        }
    }

    public _stop(): void {
        this.status?.removeEventListener('change', this.handler)
        this.status = null
    }
}
